import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Group as GroupIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { GroupInvite } from '../models/Invite';
import { useMyPendingInvites, useAcceptInvite, useDeclineInvite } from '../hooks/useQueries';

const MyInvites: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);

  const { data: invites = [], isLoading: loading } = useMyPendingInvites();
  const acceptMutation = useAcceptInvite();
  const declineMutation = useDeclineInvite();

  const handleAccept = async (invite: GroupInvite) => {
    try {
      setProcessing(invite.id);
      await acceptMutation.mutateAsync(invite.inviteToken);
      navigate(`/groups/${invite.groupId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invite');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (invite: GroupInvite) => {
    try {
      setProcessing(invite.id);
      await declineMutation.mutateAsync(invite.inviteToken);
    } catch (err: any) {
      setError(err.message || 'Failed to decline invite');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        My Invites
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {invites.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No pending invites
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When someone invites you to a group, it will appear here
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/groups')}
            sx={{ minHeight: 44 }}
          >
            Browse Groups
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2 } }}>
          {invites.map(invite => (
            <Card key={invite.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 } }}>
                    <GroupIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="h6">{invite.groupName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        icon={invite.role === 'ADMIN' ? <AdminIcon /> : <PersonIcon />}
                        label={`Join as ${invite.role}`}
                        size="small"
                        color={invite.role === 'ADMIN' ? 'primary' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Invited by {invite.invitedByUserName}
                    </Typography>
                    {invite.message && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{invite.message}"
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Expires {new Date(invite.expiresAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ 
                justifyContent: 'flex-end', 
                px: { xs: 2, sm: 2 }, 
                pb: { xs: 2, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
              }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => handleDecline(invite)}
                  disabled={processing === invite.id}
                  sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, order: { xs: 2, sm: 1 } }}
                >
                  Decline
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={() => handleAccept(invite)}
                  disabled={processing === invite.id}
                  sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, order: { xs: 1, sm: 2 } }}
                >
                  {processing === invite.id ? <CircularProgress size={24} /> : 'Accept'}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MyInvites;
